// RetryLine.tsx - v12.9.26 (#967): caption "Re-ask:" - the word retry leaves the cards (his words: "the problem is word retry which adds confusion").
// RetryLine.tsx - v12.9.0 (#696): shown on every capture step and placed above the "If not captured" ladder.
// v12.8.2 (#676/#685) · RetryLine · VoiceSpectrum AI
//
// THE SENTENCE THAT SPOKE ON HIS CALL AND EXISTED ON NO SCREEN.
//
// Tushar, looking at a ZIP step with Split ask OFF: "for zip code i don't see digit by digit then where did it
// pull off that?" He was reading the card correctly - that sentence came from the TYPE-FAILURE re-ask, a second
// narrowing mechanism with no switch, no text box and no preview. 12.8.1 made it translatable; it was still
// invisible, which he called out: "i think nothing should be hardcoded."
//
// So it appears here, showing the words that will actually be spoken.
//
// v12.9.0 (#696): ON EVERY STEP, not only where Split ask is on, and ABOVE "If not captured". His words:
// *"why it is only for split ask and not for name, zip etc.. It can be for every field."* #685(c) still holds
// for the PLATFORM's hint - "digit by digit" describes split-ask behaviour and belongs to that switch - but a
// sentence the tenant wrote in this box is theirs, and the engine now speaks it whatever the switch says.
//
// HIS SPEC, verbatim: "the caption can say Retry: if no text defined then it will just show underline with Add
// Text with hyperlink so if you click hyperlink you edit inline. if text is configured then clicking on it will
// allow to edit. no need to give preview for this line."
//
// AND THE DEFAULT IS SHOWN AS INHERITED, NOT PRE-FILLED AS A VALUE - his decision on #676. Pre-filling would
// make every default a tenant's own copy the moment they saved, and the platform could never improve a spoken
// line again for anyone who had not deliberately overridden it.

import { useState } from 'react';

export interface RetryLineProps {
  /** The tenant's own wording for this step+language, if any. */
  value?: string | null;
  /** The platform sentence that WILL be spoken when nothing is authored. Shown, never saved. */
  platformDefault?: string | null;
  disabled?: boolean;
  onChange: (next: string | null) => void;
}

export function RetryLine({ value, platformDefault, disabled, onChange }: RetryLineProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const authored = (value ?? '').trim();

  function open() {
    if (disabled) return;
    setDraft(authored || (platformDefault ?? ''));
    setEditing(true);
  }
  function commit() {
    const next = draft.trim();
    // Saving the platform's own sentence unchanged is not authoring - it stays inherited, so a later
    // improvement to the default still reaches this tenant.
    onChange(next.length === 0 || next === (platformDefault ?? '').trim() ? null : next);
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="rq-retryline rq-retryline-edit">
        <label>Re-ask:</label>
        <textarea
          className="rq-in"
          rows={2}
          autoFocus
          value={draft}
          placeholder={platformDefault ?? ''}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setEditing(false); return; }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit();
          }}
          onBlur={commit}
        />
      </div>
    );
  }

  return (
    <div className="rq-retryline">
      <label>Re-ask:</label>
      {authored
        ? <button type="button" className="rq-retryline-text" onClick={open} disabled={disabled}>{authored}</button>
        : (
          <>
            <button type="button" className="rq-retryline-add" onClick={open} disabled={disabled}>Add text</button>
            {platformDefault && <span className="rq-retryline-inherit">&mdash; using the platform default: &ldquo;{platformDefault}&rdquo;</span>}
          </>
        )}
    </div>
  );
}
